<template>
  <hb-basic-page :loading="loading" :subtle-loading="subtleLoading" hide-app-bar name="onboarding-view">
    <v-col v-if="!loading" class="pb-10" cols="12" lg="10" offset-lg="1" offset-xl="2" xl="8">
      <v-row>
        <v-col cols="12">
          <v-stepper v-model="currentStep" :alt-labels="$vuetify.breakpoint.mdAndUp" class="background" flat>
            <v-stepper-header class="elevation-0">
              <v-stepper-step :complete="currentStep > 1" step="1">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.company_details') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 2" step="2">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.introduction') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 3" step="3">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.create_teams') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 4" step="4">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.create_personnel_survey') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 5" step="5">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.create_management_survey') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 6" step="6">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.create_tasks') }}
                </div>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep === 7" step="7">
                <div class="text-center text-body-2 px-2">
                  {{ $t('onboarding.done') }}
                </div>
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </v-col>
        <v-col cols="12">
          <v-card class="transparent">
            <v-card-text class="pt-10 pb-10">
              <v-fade-transition group hide-on-leave>
                <v-row v-if="currentStep === 2" key="2" class="mt-xl-16" no-gutters>
                  <v-col cols="12">
                    <introduction @next-step="goToNextStep"/>
                  </v-col>
                </v-row>
                <v-row v-if="currentStep === 3" key="3" class="mt-xl-16" no-gutters>
                  <v-col cols="12">
                    <create-teams :teams="teams" @next-step="goToNextStep" @refresh-teams="reloadTeams"/>
                  </v-col>
                </v-row>
                <v-row v-if="currentStep === 4" key="4" no-gutters>
                  <v-col cols="12">
                    <create-personnel-analysis :teams="teams" @next-step="goToNextStep" @refresh-teams="reloadTeams"/>
                  </v-col>
                </v-row>
                <v-row v-if="currentStep === 5" key="5" no-gutters>
                  <v-col key="5" cols="12">
                    <create-management-analysis :teams="teams" @next-step="goToNextStep" @refresh-teams="reloadTeams"/>
                  </v-col>
                </v-row>
                <v-row v-if="currentStep === 6" key="6" no-gutters>
                  <v-col cols="12">
                    <create-tasks @next-step="goToNextStep"/>
                  </v-col>
                </v-row>
                <v-row v-if="currentStep === 7" key="7" class="mt-xl-16" no-gutters>
                  <v-col cols="12">
                    <all-done/>
                  </v-col>
                </v-row>
              </v-fade-transition>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </hb-basic-page>
</template>

<script>
import Introduction from '@/components/Tenant/Onboarding/Introduction';
import CreateTeams from '@/components/Tenant/Onboarding/CreateTeams';
import CreatePersonnelAnalysis from '@/components/Tenant/Onboarding/CreatePersonnelAnalysis';
import CreateManagementAnalysis from '@/components/Tenant/Onboarding/CreateManagementAnalysis';
import CreateTasks from '@/components/Tenant/Onboarding/CreateTasks';
import AllDone from '@/components/Tenant/Onboarding/AllDone';
import teamApi from '@/api/team';
import tenantApi, {TenantValue} from '@/api/tenant';
import UserService from '@/service/user.service';
import userApi from '@/api/user';

export default {
  name: 'OnboardingView',
  components: {
    AllDone,
    CreateTasks,
    CreateManagementAnalysis,
    CreatePersonnelAnalysis,
    CreateTeams,
    Introduction,
  },
  props: ['step'],
  data() {
    return {
      loading: true,
      subtleLoading: false,
      currentStep: 2,
      teams: null,
    };
  },
  methods: {
    goToNextStep() {
      let nextStep = this.currentStep + 1;
      this.$router.replace({name: 'tenant_onboarding', params: {step: nextStep + ''}});
      this.currentStep = nextStep;
      this.saveTenantStep();
    },
    async saveTenantStep() {
      try {
        await tenantApi.createOrUpdateTenantValue(new TenantValue('onboarding', this.currentStep, this.$tenant));
        const me = await userApi.getMe();
        UserService.setUserInfo(me);
      } catch (e) {
        this.$handleApiError(e);
      }
    },
    async reloadTeams() {
      try {
        const response = await teamApi.getTeams();
        if (response) {
          this.teams = response;
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.create_error'));
      }
    },
  },
  async mounted() {
    await this.reloadTeams();
    this.loading = false;
    if (this.step) {
      this.currentStep = parseInt(this.step);
    } else {
      const step = this.$currentTenant?.values?.find(v => v.key === 'onboarding');
      if (step) {
        const stepValue = parseInt(step.value);
        if (stepValue) {
          if (stepValue === -1 || stepValue === 7) this.$router.replace({name: 'tenant_start'});
          this.currentStep = parseInt(step.value);
        }
      } else {
        await this.saveTenantStep();
      }
    }
  },
};
</script>

<style scoped></style>
